import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { api, KYVIXE } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = React.useState("");
  const [website, setWebsite] = React.useState(""); // honeypot
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email, website });
      toast.success("You are on the list.");
      setDone(true);
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="border-t border-white/10 bg-[#0a0a0f] pt-16 pb-8" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2 space-y-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-display text-2xl font-bold lowercase tracking-tighter">
              kyvixe<span className="text-blue-500">.</span>
            </span>
          </Link>
          <p className="text-gray-400 max-w-md text-sm leading-relaxed">
            we build ai-powered websites, chatbots and automations that turn
            every customer message into revenue. build. automate. scale.
          </p>
          <div className="flex items-center gap-3 pt-2">
            <a
              href={KYVIXE.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-instagram-link"
              className="p-2 rounded-full border border-white/10 text-gray-300 hover:text-blue-400 hover:border-blue-500/60 transition"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              href={KYVIXE.facebook}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-facebook-link"
              className="p-2 rounded-full border border-white/10 text-gray-300 hover:text-blue-400 hover:border-blue-500/60 transition"
              aria-label="Facebook"
            >
              <Facebook size={18} />
            </a>
            <a
              href={`mailto:${KYVIXE.email}`}
              data-testid="footer-email-link"
              className="p-2 rounded-full border border-white/10 text-gray-300 hover:text-blue-400 hover:border-blue-500/60 transition"
              aria-label="Email"
            >
              <Mail size={18} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-display lowercase font-bold text-lg mb-4">quick links</h4>
          <ul className="space-y-2 text-sm text-gray-400">
            <li><Link to="/services" className="hover:text-blue-400">services</Link></li>
            <li><Link to="/industries" className="hover:text-blue-400">industries</Link></li>
            <li><Link to="/blog" className="hover:text-blue-400">blog</Link></li>
            <li><Link to="/faq" className="hover:text-blue-400">faqs</Link></li>
            <li><Link to="/contact" className="hover:text-blue-400">contact</Link></li>
            <li><Link to="/admin/login" className="hover:text-blue-400">admin</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display lowercase font-bold text-lg mb-4">newsletter</h4>
          <p className="text-sm text-gray-400 mb-3">
            monthly tips on ai, automation and websites that convert.
          </p>
          {done ? (
            <div className="flex items-center gap-2 text-blue-400 text-sm" data-testid="newsletter-success">
              <Check size={16} /> you are on the list
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@work.com"
                className="w-full bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-blue-500/60"
                data-testid="newsletter-email-input"
              />
              <input
                type="text"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />
              <button
                type="submit"
                disabled={loading}
                className="kyv-btn-primary text-sm disabled:opacity-60"
                data-testid="newsletter-submit-btn"
              >
                {loading ? "subscribing…" : "subscribe"}
              </button>
            </form>
          )}
          <a
            href={`mailto:${KYVIXE.email}`}
            className="block mt-4 text-sm text-gray-400 hover:text-blue-400"
            data-testid="footer-email-text"
          >
            {KYVIXE.email}
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-500">
        <p>© {new Date().getFullYear()} kyvixe. all rights reserved.</p>
        <p>build. automate. scale.</p>
      </div>
    </footer>
  );
}
